import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Anchor, Shield, Star, Users } from 'lucide-react';
import { WhatsappLogo, Waves, Sun } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import TourCard from '../components/TourCard';
import ReviewCard from '../components/ReviewCard';
import StatsCounter from '../components/StatsCounter';
import { publicApi } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';

const VIDEO_URL = "https://customer-assets.emergentagent.com/job_groove-tours/artifacts/w7rb714j_cosonvillage_20260212_1.mp4";
const FALLBACK_IMAGE = "https://images.unsplash.com/photo-1697302608127-9ae4920dae05?w=1920";

export default function HomePage() {
  const [tours, setTours] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await publicApi.seed().catch(() => {});
        const [toursRes, reviewsRes, settingsRes] = await Promise.all([
          publicApi.getTours(),
          publicApi.getReviews(),
          publicApi.getSettings()
        ]);
        setTours(toursRes.data);
        setReviews(reviewsRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const features = [
    { icon: Shield, title: t('feature_transport'), description: t('feature_transport_desc'), color: '#FF6B4A' },
    { icon: Users, title: t('feature_guides'), description: t('feature_guides_desc'), color: '#00A896' },
    { icon: Star, title: t('feature_safety'), description: t('feature_safety_desc'), color: '#FF6B4A' },
    { icon: Anchor, title: t('feature_reviews'), description: t('feature_reviews_desc'), color: '#00A896' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.23, 1, 0.32, 1] } }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="home-page">
      <Navbar />
      
      {/* Hero Section - Video Only */}
      <section className="relative h-screen min-h-[500px] overflow-hidden">
        {/* Video/Image Background - Full Screen */}
        {!videoError ? (
          <video
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay muted loop playsInline
            onError={() => setVideoError(true)}
          >
            <source src={VIDEO_URL} type="video/mp4" />
          </video>
        ) : (
          <img src={FALLBACK_IMAGE} alt="Caribbean Beach" className="absolute inset-0 w-full h-full object-cover" />
        )}
        
        {/* Subtle gradient at bottom for transition */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#FAFAF8] to-transparent" />

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-12 left-1/2 -translate-x-1/2 z-10"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-8 h-14 rounded-full border-2 border-white/50 flex justify-center pt-3 backdrop-blur-sm">
            <div className="w-1.5 h-3 bg-white rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Hero Text Section - Below Video */}
      <section className="py-16 md:py-24 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="flex items-center justify-center gap-2 mb-6">
              <Waves weight="duotone" className="w-6 h-6 text-[#00A896]" />
              <span className="text-[#00A896] font-medium tracking-wide uppercase text-sm">
                {t('hero_subtitle')}
              </span>
            </motion.div>
            
            <motion.h1 
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl font-serif text-[#012A36] mb-6 leading-[1.1]"
            >
              {t('hero_title')}
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-xl text-[#4A6B74] mb-10 leading-relaxed max-w-2xl mx-auto"
            >
              {t('hero_description')}
            </motion.p>
            
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/excursiones">
                <motion.button 
                  className="coral-btn px-8 py-4 text-lg flex items-center gap-2"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="hero-btn-tours"
                >
                  {t('hero_btn_tours')}
                  <ChevronRight className="w-5 h-5" />
                </motion.button>
              </Link>
              <a 
                href={`https://wa.me/${settings?.contact?.whatsapp?.replace(/\D/g, '') || '18091234567'}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <motion.button 
                  className="whatsapp-btn flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="hero-btn-reserve"
                >
                  <WhatsappLogo weight="fill" className="w-5 h-5" />
                  {t('hero_btn_reserve')}
                </motion.button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Counter */}
      <StatsCounter />

      {/* Tours Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="tours-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="section-divider" />
              <span className="text-[#FF6B4A] font-medium tracking-wide uppercase text-sm">
                {t('home_tours_subtitle')}
              </span>
            </div>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <h2 className="text-4xl md:text-5xl font-serif text-[#012A36] mb-4">
                  {t('home_tours_title')}
                </h2>
                <p className="text-[#4A6B74] max-w-xl text-lg">
                  {t('home_tours_description')}
                </p>
              </div>
              <Link to="/excursiones">
                <Button 
                  variant="outline" 
                  className="rounded-full border-2 border-[#012A36] text-[#012A36] hover:bg-[#012A36] hover:text-white px-6"
                >
                  {t('tours_view_all')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1,2,3].map(i => (
                <div key={i} className="bg-[#FAFAF8] rounded-[2rem] h-[450px] animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tours.filter(t => t.is_published).slice(0, 6).map((tour, index) => (
                <TourCard key={tour.id} tour={tour} index={index} />
              ))}
            </div>
          )}

          {tours.length > 6 && (
            <motion.div 
              className="text-center mt-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <Link to="/excursiones">
                <Button className="coral-btn px-8 py-4 text-lg">
                  {t('tours_view_all')} ({tours.length} tours)
                </Button>
              </Link>
            </motion.div>
          )}
        </div>
      </section>

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <section className="py-20 md:py-28 bg-[#012A36] noise-overlay relative">
          <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#00A896] font-medium tracking-wide uppercase text-sm">
                {t('home_reviews_subtitle')}
              </span>
              <h2 className="text-4xl md:text-5xl font-serif text-white mt-4 mb-4">
                {t('home_reviews_title')}
              </h2>
              <div className="flex justify-center gap-1 mb-4">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-6 h-6 fill-[#FF6B4A] text-[#FF6B4A]" />
                ))}
              </div>
              <p className="text-white/60">4.9 {t('home_reviews_rating')}</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {reviews.slice(0, 3).map((review, index) => (
                <motion.div
                  key={review.id || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <ReviewCard review={review} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-[#00A896] to-[#008f80] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full bg-white blur-3xl" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-serif text-white mb-6">
              {t('cta_title')}
            </h2>
            <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
              {t('cta_description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/excursiones">
                <motion.button 
                  className="bg-white text-[#00A896] font-semibold rounded-full px-8 py-4 text-lg hover:bg-white/90 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {t('cta_btn_tours')}
                </motion.button>
              </Link>
              <a 
                href={`https://wa.me/${settings?.contact?.whatsapp?.replace(/\D/g, '') || '18091234567'}`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <motion.button 
                  className="whatsapp-btn flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <WhatsappLogo weight="fill" className="w-5 h-5" />
                  {t('cta_btn_whatsapp')}
                </motion.button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section - Moved to bottom before footer */}
      <section className="py-20 md:py-28 bg-[#FAFAF8] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#00A896] font-medium tracking-wide uppercase text-sm">
              {t('about_subtitle') || '¿Por qué elegirnos?'}
            </span>
            <h2 className="text-4xl md:text-5xl font-serif text-[#012A36] mt-4">
              {t('about_title') || 'Quiénes Somos'}
            </h2>
          </motion.div>
          
          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                variants={itemVariants}
                className="text-center p-8 rounded-[2rem] bg-white hover:shadow-lg transition-shadow duration-500"
              >
                <div 
                  className="w-16 h-16 mx-auto mb-5 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: `${feature.color}15` }}
                >
                  <feature.icon className="w-8 h-8" style={{ color: feature.color }} />
                </div>
                <h3 className="font-semibold text-[#012A36] text-lg mb-2">{feature.title}</h3>
                <p className="text-[#4A6B74] text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
